import {
  BearingEnvelope,
  FeaturePatternTrussContainer,
  RoofPlane,
  TrussEnvelope,
  WorkLine,
} from '@paragon/contract-library';
import { calculateFloorPolyline2DShape } from '@paragon/layout';

import type { RootState } from '../../store';
import { getBoundedRoofAndCeilingPlaneShapesByGuid } from '../../store/selectors/roofAndCeilingPlaneSelectors';

/**
 * A short, stable label for layout objects that have no name, such as roof planes.
 */
export function shortId(guid: string) {
  return guid.slice(0, 8);
}

const round = (value: number) => Math.round(value * 16) / 16;
const roundPoint = <T extends { x: number; y: number; z?: number }>(point: T) =>
  point.z == null
    ? { x: round(point.x), y: round(point.y) }
    : { x: round(point.x), y: round(point.y), z: round(point.z) };

/**
 * A roof plane as the Assistant reads it: what it sits on, what cuts it, and its outline as bounded by those cuts, in
 * plan coordinates with z the elevation, in inches.
 */
export function describeRoofPlane(state: RootState, plane: RoofPlane) {
  const { bearingEnvelopes } = state.layout;
  let outlines: { x: number; y: number; z?: number }[][] | null = null;
  try {
    const shape = getBoundedRoofAndCeilingPlaneShapesByGuid(state)[plane.guid];
    outlines = shape == null || shape.length === 0 ? null : shape.map((polygon) => polygon.vertices.map(roundPoint));
  } catch {
    // A plane the app can't bound yet, such as one whose cuts don't close it, has no outline.
  }
  return {
    id: shortId(plane.guid),
    ...(plane.geometry.type === 'BearingEnvelope'
      ? { onWall: bearingEnvelopes[plane.geometry.bearingEnvelopeGuid]?.name ?? null }
      : {
          base: {
            from: roundPoint(plane.geometry.segment.basePoint),
            to: roundPoint(plane.geometry.segment.endPoint),
            elevation: round(plane.geometry.elevation),
          },
        }),
    slopeInDegrees: plane.slopeInDegrees,
    overhang: plane.overhang,
    cuts: plane.cuts.map((cut) =>
      cut.type === 'AgainstPlane'
        ? { againstPlane: shortId(cut.cuttingPlaneGuid) }
        : { along: { from: roundPoint(cut.segment.basePoint), to: roundPoint(cut.segment.endPoint) } },
    ),
    // Usually one polygon; a plane split by its cuts has more. Until cuts close a plane off, it runs on without end.
    ...(outlines != null ? { outlines } : { bounded: false }),
  };
}

/**
 * A floor area with its outline in plan, in inches.
 */
export function describeFloorArea(state: RootState, container: FeaturePatternTrussContainer) {
  let outline: { x: number; y: number }[] | null = null;
  try {
    outline =
      calculateFloorPolyline2DShape(container.shape, state.layout.bearingEnvelopes)?.vertices.map(roundPoint) ?? null;
  } catch {
    // Not closed yet.
  }
  return {
    name: container.name,
    bottomElevation: container.shape.bottomElevation,
    topElevation: container.shape.topElevation,
    outline,
  };
}

/**
 * A wall or beam, in plan coordinates in inches.
 */
export function describeWall(envelope: BearingEnvelope) {
  return {
    name: envelope.name,
    type: envelope.bearingType,
    from: envelope.leftPoint,
    to: envelope.rightPoint,
    thickness: envelope.thickness,
    top: envelope.top,
    bottom: envelope.bottom,
  };
}

/**
 * A truss envelope: where it runs in plan, in inches, and the truss and floor area or imported roof it belongs to.
 */
export function describeTrussEnvelope(state: RootState, envelope: TrussEnvelope) {
  const { featurePatternContainers, roofContainers, trussEnvelopeErrors } = state.layout;
  const componentDesigns = state.componentDesigns.data;
  return {
    name: envelope.name,
    type: envelope.componentType,
    from: envelope.leftPoint,
    to: envelope.rightPoint,
    elevation: envelope.elevation,
    plies: envelope.numberOfPlies,
    leftOverhang: envelope.leftOverhang,
    rightOverhang: envelope.rightOverhang,
    // Part of a cap set: a base truss under piggyback caps, or a cap on one.
    ...(envelope.containerLevel !== 'NotCapped'
      ? {
          capSet:
            envelope.containerLevel === 'BaseLevel'
              ? { part: 'base', capsAt: envelope.capLevelOne }
              : {
                  part: 'cap',
                  from: envelope.containerLevel === 'CapLevelOne' ? envelope.capLevelOne : envelope.capLevelTwo,
                },
        }
      : {}),
    // Valley trusses sit on the roof planes that cut them from below.
    ...(envelope.bottomCuts.length > 0
      ? {
          sitsOnRoofPlanes: envelope.bottomCuts.flatMap((cut) =>
            cut.type === 'RoofPlane' ? [shortId(cut.roofPlaneGuid)] : [],
          ),
        }
      : {}),
    floorArea:
      envelope.featurePatternContainerGuid == null
        ? null
        : (featurePatternContainers[envelope.featurePatternContainerGuid]?.name ?? null),
    importedRoof:
      envelope.roofContainerGuid == null ? null : (roofContainers[envelope.roofContainerGuid]?.name ?? null),
    truss:
      envelope.componentDesignGuid == null
        ? null
        : (componentDesigns[envelope.componentDesignGuid]?.component.name ?? null),
    error: trussEnvelopeErrors[envelope.guid] ?? null,
  };
}

export function describeWorkLine(line: WorkLine) {
  return { name: line.name, bounding: line.bounding, geometry: line.geometry };
}
